import { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function WhatsNewModal() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Wait until the user has answered the email-consent banner before
    // stacking What's New on top of it — one prompt at a time.
    if (!user.email_consent_recorded_at) return;
    // Also wait for the higher-priority beta acknowledgment.
    if ((user.beta_ack_version_accepted ?? 0) < (user.beta_ack_required_version ?? 1)) return;
    let alive = true;
    api.get("/announcements/mine")
      .then((r) => { if (alive) setItems(r.data || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [user]);

  if (!user || items.length === 0) return null;
  const current = items[0];

  const dismiss = async () => {
    setDismissing(true);
    try {
      await api.post(`/announcements/${current.id}/dismiss`);
      setItems((xs) => xs.filter((x) => x.id !== current.id));
    } catch { /* noop — user can retry */ }
    finally { setDismissing(false); }
  };

  const learnMore = () => {
    dismiss();
    if (current.learn_more_url) {
      window.location.href = current.learn_more_url;
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[95] flex items-end sm:items-center justify-center p-4"
      data-testid="whats-new-modal"
    >
      <div className="absolute inset-0 bg-foreground/45 backdrop-blur-[2px]" onClick={dismiss} />
      <div className="relative max-w-md w-full rounded-2xl border border-border bg-[hsl(var(--sb-cream))] shadow-xl p-6 sm:p-7">
        <button
          type="button"
          onClick={dismiss}
          disabled={dismissing}
          className="absolute right-3 top-3 rounded-full p-1.5 text-muted-foreground hover:bg-muted transition-colors"
          data-testid="whats-new-close"
          aria-label="Dismiss"
        >
          <X size={16} />
        </button>
        <div className="inline-flex items-center gap-2">
          <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-[hsl(var(--sb-clay))]/15 text-[hsl(var(--sb-clay))]">
            <Sparkles size={16} />
          </span>
          <span className="text-[11px] uppercase tracking-[0.16em] font-medium text-[hsl(var(--sb-clay))]">
            What&apos;s New
          </span>
        </div>
        <h2 className="mt-4 font-serif text-2xl leading-tight" data-testid="whats-new-title">
          {current.title}
        </h2>
        <p className="mt-3 text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
          {current.body}
        </p>
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          {current.learn_more_url && (
            <button
              type="button"
              onClick={learnMore}
              className="btn-ghost border"
              data-testid="whats-new-learn-more"
            >
              Learn more
            </button>
          )}
          <button
            type="button"
            onClick={dismiss}
            disabled={dismissing}
            className="btn-primary"
            data-testid="whats-new-dismiss"
          >
            {dismissing ? "Dismissing…" : "Got it"}
          </button>
        </div>
      </div>
    </div>
  );
}
